(function () {
  const DEBUG = false;

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const norm = (v) => String(v ?? "").trim().toLowerCase();

  function log() {
    if (!DEBUG) return;
    console.log("[PVSEL]", ...arguments);
  }

  function groupLog(label, value) {
    if (!DEBUG) return;
    console.groupCollapsed("[PVSEL] " + label);
    console.log(value);
    console.groupEnd();
  }

  function pctOff(compare, price) {
    compare = parseInt(compare || 0, 10);
    price = parseInt(price || 0, 10);
    if (!compare || compare <= price) return 0;
    return Math.round(((compare - price) / compare) * 100);
  }

  function getVariantLabelValue(ov) {
    if (!ov) return "";
    return ov.label || ov.value_key || ov.title || "";
  }

  function isColorOptionTitle(optTitle) {
    const key = matchNorm(optTitle);
    return key.includes("farbe") || key.includes("color") || key.includes("colour") || key.includes("couleur");
  }

  function getVariantColorNumber(variant) {
    const value = variant?.color_number;
    if (value == null) return "";
    return String(value).trim();
  }

  function formatColorOptionLabel(label, colorNumber, overrides) {
    const cleanNumber = String(colorNumber || "").trim();
    const cleanLabel = String(overrides?.[cleanNumber] || label || "").trim();
    if (!cleanLabel || !cleanNumber) return cleanLabel;
    if (cleanLabel.endsWith(`(${cleanNumber})`)) return cleanLabel;
    return `${cleanLabel} (${cleanNumber})`;
  }

  function getAliases(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      return value
        .split(/\r?\n|,/)
        .map((x) => x.trim())
        .filter(Boolean);
    }
    return [];
  }

  function matchNorm(value) {
    return String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ß/g, "ss")
      .replace(/ẞ/g, "ss")
      .toLowerCase()
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  function compactMatchNorm(value) {
    return matchNorm(value).replace(/\s+/g, "");
  }

  function optionValueMatchKeys(ov) {
    return [
      ov?.label,
      ov?.value_key,
      ov?.title,
      ov?.short_label,
      ...getAliases(ov?.aliases)
    ]
      .map((value) => [matchNorm(value), compactMatchNorm(value)])
      .flat()
      .filter(Boolean);
  }

  function optionValueMatchesNative(ov, nativeValue) {
    const nativeKeys = new Set([matchNorm(nativeValue), compactMatchNorm(nativeValue)].filter(Boolean));
    if (!nativeKeys.size) return false;
    return optionValueMatchKeys(ov).some((key) => nativeKeys.has(key));
  }

  function getNativeVariantOptionArray(variant) {
    if (!variant) return [];

    if (Array.isArray(variant.options) && variant.options.length) {
      return variant.options.map((x) => String(x ?? ""));
    }

    const arr = [];
    if (variant.option1 != null) arr.push(String(variant.option1));
    if (variant.option2 != null) arr.push(String(variant.option2));
    if (variant.option3 != null) arr.push(String(variant.option3));
    return arr;
  }

  function hasRichOptionValues(variants) {
    return (variants || []).some((v) => Array.isArray(v.option_values) && v.option_values.length);
  }

  function getProductOptionNames(data, variants) {
    if (Array.isArray(data?.productOptions) && data.productOptions.length) {
      return data.productOptions.map((x) => String(x || "").trim()).filter(Boolean);
    }

    if (Array.isArray(data?.options) && data.options.length) {
      return data.options.map((x) => String(x || "").trim()).filter(Boolean);
    }

    const first = (variants || [])[0];
    if (!first) return [];

    if (Array.isArray(first.options) && first.options.length) {
      if (Array.isArray(first.option_values) && first.option_values.length) {
        const names = first.option_values.map((ov, i) => String(ov?.option?.title || `Option ${i + 1}`).trim());
        if (names.length) return names;
      }
      return first.options.map((_, i) => `Option ${i + 1}`);
    }

    const fallback = [];
    if (first.option1 != null) fallback.push("Option 1");
    if (first.option2 != null) fallback.push("Option 2");
    if (first.option3 != null) fallback.push("Option 3");
    return fallback;
  }

  function getVariantOptionArray(variant, optionNames) {
    if (!variant) return [];

    const nativeOptions = getNativeVariantOptionArray(variant);
    if (nativeOptions.length) return nativeOptions;

    if (Array.isArray(variant.option_values) && variant.option_values.length) {
      return optionNames.map((name, idx) => {
        const ov =
          variant.option_values.find((x) => norm(x?.option?.title) === norm(name)) ||
          variant.option_values[idx];
        return ov ? getVariantLabelValue(ov) : "";
      });
    }

    return [];
  }

  function makeFallbackOptionValue(optTitle, index, value, sortOrder) {
    return {
      label: value,
      title: value,
      value_key: value,
      sort_order: sortOrder,
      is_active: true,
      option: {
        title: optTitle,
        key: optTitle,
        sort_order: index
      },
      _fallback: true
    };
  }

  function getRichOptionForNativeValue(variant, optTitle, nativeValue, optionIndex, optionNames) {
    const optionValues = variant?.option_values || [];
    if (!Array.isArray(optionValues) || !optionValues.length) return null;

    const matchingOptionValues = optionValues.filter((ov) => norm(ov?.option?.title) === norm(optTitle));
    const valueMatchedOption =
      matchingOptionValues.find((ov) => optionValueMatchesNative(ov, nativeValue)) ||
      optionValues.find((ov) => optionValueMatchesNative(ov, nativeValue));

    if (valueMatchedOption) return valueMatchedOption;

    if (matchingOptionValues.length === 1) return matchingOptionValues[0];

    if (
      Number.isInteger(optionIndex) &&
      Array.isArray(optionNames) &&
      optionNames.length &&
      optionValues.length === optionNames.length &&
      optionValues[optionIndex]
    ) {
      return optionValues[optionIndex];
    }

    if (optionValues.length === 1) return optionValues[0];

    return matchingOptionValues[0] || null;
  }

  function decorateNativeOptionValue(base, richValue) {
    if (!richValue) return base;

    return Object.assign({}, richValue, {
      label: base.label,
      title: base.title,
      value_key: base.value_key,
      option: Object.assign({}, richValue.option || {}, base.option),
      _native_value: true
    });
  }

  function groupOptionValuesRich(variants, optionNames) {
    if (!optionNames.length) return [];

    const groups = new Map();

    for (const v of variants) {
      const nativeOptions = getNativeVariantOptionArray(v);
      if (!nativeOptions.length) continue;

      optionNames.forEach((optTitle, index) => {
        const nativeValue = nativeOptions[index];
        if (nativeValue == null || nativeValue === "") return;

        const richValue = getRichOptionForNativeValue(v, optTitle, nativeValue, index, optionNames);
        if (richValue && richValue.is_active === false) return;

        const ov = decorateNativeOptionValue(
          makeFallbackOptionValue(optTitle, index, String(nativeValue), groups.get(norm(optTitle))?.values?.size || 0),
          richValue
        );
        const k = norm(optTitle);
        if (!groups.has(k)) {
          groups.set(k, {
            option: ov.option || { title: optTitle, key: optTitle, sort_order: index },
            values: new Map(),
            optTitle
          });
        }

        const g = groups.get(k);
        const vKey = norm(ov.value_key || ov.label || ov.title);
        const mapKey = `${norm(ov.option?.key || ov.option?.title)}::${vKey}`;

        if (!g.values.has(mapKey)) g.values.set(mapKey, ov);
      });
    }

    const list = Array.from(groups.values()).sort(
      (a, b) => (a.option?.sort_order || 0) - (b.option?.sort_order || 0)
    );

    for (const g of list) {
      g.valuesSorted = Array.from(g.values.values()).sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
      );
      g.mode = "rich";
    }

    return list;
  }

  function groupOptionValuesFallback(variants, optionNames) {
    const groups = [];

    optionNames.forEach((optTitle, index) => {
      const map = new Map();

      for (const v of variants) {
        const arr = getVariantOptionArray(v, optionNames);
        const rawValue = arr[index];

        if (DEBUG) {
          log("fallback variant option scan", {
            variantId: v?.id,
            optionNames,
            optionArray: arr,
            index,
            optTitle,
            rawValue
          });
        }

        if (rawValue == null || rawValue === "") continue;

        const value = String(rawValue);
        const key = norm(value);

        if (!map.has(key)) {
          map.set(key, makeFallbackOptionValue(optTitle, index, value, map.size));
        }
      }

      const valuesSorted = Array.from(map.values());

      groups.push({
        option: { title: optTitle, key: optTitle, sort_order: index },
        valuesSorted,
        optTitle,
        mode: "fallback"
      });
    });

    return groups;
  }

  function groupOptionValues(variants, data) {
    const optionNames = getProductOptionNames(data, variants);
    if (optionNames.length === 1) {
      const values = variants.map((variant) => getNativeVariantOptionArray(variant)[0]);
      if (new Set(values.map(norm)).size < values.length) {
        const optTitle = optionNames[0];
        const valuesSorted = variants.map((variant, position) => {
          const value = getNativeVariantOptionArray(variant)[0];
          const rich = getRichOptionForNativeValue(variant, optTitle, value, 0, optionNames);
          return Object.assign(
            decorateNativeOptionValue(makeFallbackOptionValue(optTitle, 0, value, position), rich),
            { _variant_id: String(variant.id) }
          );
        }).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        return [{ option: valuesSorted[0].option, valuesSorted, optTitle, mode: "variant-id" }];
      }
    }

    log("resolved option names", optionNames);
    log("has rich option values", hasRichOptionValues(variants));

    if (hasRichOptionValues(variants)) {
      const richGroups = groupOptionValuesRich(variants, optionNames);
      groupLog("rich groups", richGroups);
      if (richGroups.length) return richGroups;
    }

    const fallbackGroups = groupOptionValuesFallback(variants, optionNames);
    groupLog("fallback groups", fallbackGroups);
    return fallbackGroups;
  }

  function buildVariantIndex(variants, data) {
    const optionNames = getProductOptionNames(data, variants);
    const map = new Map();

    for (const v of variants) {
      let pairs = [];

      if (optionNames.length) {
        const arr = getVariantOptionArray(v, optionNames);
        pairs = arr
          .map((val, i) => `${norm(optionNames[i] || `Option ${i + 1}`)}=${norm(val)}`)
          .sort();
      } else if (Array.isArray(v.option_values) && v.option_values.length) {
        pairs = v.option_values
          .filter((ov) => ov && ov.is_active !== false)
          .map((ov) => `${norm(ov.option?.title)}=${norm(getVariantLabelValue(ov))}`)
          .sort();
      } else {
        const arr = getVariantOptionArray(v, optionNames);
        pairs = arr
          .map((val, i) => `${norm(optionNames[i] || `Option ${i + 1}`)}=${norm(val)}`)
          .sort();
      }

      const key = pairs.join("|");
      map.set(key, v);

      if (DEBUG) {
        log("variant index row", {
          variantId: v?.id,
          key,
          pairs,
          variant: v
        });
      }
    }

    return map;
  }

  function findVariant(index, selection) {
    const pairs = Object.keys(selection)
      .map((k) => `${norm(k)}=${norm(selection[k])}`)
      .sort()
      .join("|");

    const found = index.get(pairs) || null;

    if (DEBUG) {
      log("findVariant", {
        selection,
        key: pairs,
        foundVariantId: found?.id || null
      });
    }

    return found;
  }

  function syncVariantId(formId, variantId) {
    const form = document.getElementById(formId);
    if (!form) {
      log("form not found for syncVariantId", formId);
      return false;
    }

    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) {
      log("id input not found in form", form);
      return false;
    }

    const nextId = String(variantId || "");
    if (!nextId) return false;

    if (idInput.value !== nextId) idInput.value = nextId;

    idInput.dispatchEvent(new Event("change", { bubbles: true }));
    form.dispatchEvent(new Event("change", { bubbles: true }));
    log("synced variant id", { formId, variantId: nextId });
    return true;
  }

  const FETCH_STATE = new WeakMap();

  function getProductInfo(root) {
    return root.closest("product-info") || document.querySelector("product-info");
  }

  function getSectionIds(productInfo) {
    const sectionId = productInfo?.dataset?.originalSection || productInfo?.dataset?.section || "";
    const destSection = productInfo?.dataset?.section || sectionId || "";
    return { sectionId, destSection };
  }

  function getProductUrl(productInfo) {
    return productInfo?.dataset?.url || productInfo?.getAttribute("data-url") || "";
  }

  function updateURL(productInfo, url, variantId) {
    if (!url) return;
    const full = `${url}${variantId ? `?variant=${variantId}` : ""}`;

    try {
      productInfo?.querySelector("share-button")?.updateUrl?.(`${window.shopUrl || ""}${full}`);
    } catch (e) {}

    if (productInfo?.dataset?.updateUrl === "false") return;

    try {
      window.history.replaceState({}, "", full);
    } catch (e) {}
  }

  function updateVariantInputsInProductInfo(productInfo, variantId) {
    if (!productInfo) return;
    const destSection = productInfo.dataset.section;

    productInfo
      .querySelectorAll(`#product-form-${destSection}, #product-form-installment-${destSection}`)
      .forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variantId ?? "";
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
  }

  function dispatchPvselVariantChange(root, variant, preferredSrc) {
    if (!variant || variant.id == null) return;

    document.dispatchEvent(
      new CustomEvent("pvsel:variant-change", {
        detail: {
          root,
          variant,
          variantId: String(variant.id),
          preferredSrc: preferredSrc || null
        }
      })
    );
  }

  function updateFromFetchedHTML(productInfo, html, sectionId, destSection) {
    const updateSourceFromDestination = (id, shouldHide = () => false) => {
      const source = html.getElementById(`${id}-${sectionId}`);
      const destination = productInfo.querySelector(`#${id}-${destSection}`);
      if (source && destination) {
        destination.innerHTML = source.innerHTML;
        destination.classList.toggle("hidden", shouldHide(source));
      }
    };

    updateSourceFromDestination("price");
    updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));
    updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");
    updateSourceFromDestination("Volume");
    updateSourceFromDestination("Price-Per-Item", ({ classList }) => classList.contains("hidden"));

    productInfo.querySelector(`#Quantity-Rules-${destSection}`)?.classList.remove("hidden");
    productInfo.querySelector(`#Volume-Note-${destSection}`)?.classList.remove("hidden");

    const sourceForm = html.querySelector('product-form');
    const destinationForm = productInfo.querySelector('product-form');
    if (sourceForm && destinationForm) {
      const setIsIncomplete = sourceForm.hasAttribute('data-pdp-variation-set-missing');
      destinationForm.toggleAttribute('data-pdp-variation-set-missing', setIsIncomplete);

      const sourceVariantInput = sourceForm.querySelector('input[name="id"]');
      const destinationVariantInput = destinationForm.querySelector('input[name="id"]');
      if (sourceVariantInput && destinationVariantInput) {
        destinationVariantInput.disabled = sourceVariantInput.disabled;
      }

      const sourceSubmit = sourceForm.querySelector('[name="add"]');
      const destinationSubmit = destinationForm.querySelector('[name="add"]');
      if (sourceSubmit && destinationSubmit) {
        destinationSubmit.disabled = sourceSubmit.disabled;
        destinationSubmit.toggleAttribute('aria-disabled', sourceSubmit.hasAttribute('aria-disabled'));
        const sourceLabel = sourceSubmit.querySelector('span');
        const destinationLabel = destinationSubmit.querySelector('span');
        if (sourceLabel && destinationLabel) destinationLabel.textContent = sourceLabel.textContent;
      }
    }

    const currentUpsell = productInfo.querySelector("[data-upsell]");
    const nextUpsell = html.querySelector("[data-upsell]");

    if (currentUpsell && nextUpsell) {
      currentUpsell.replaceWith(nextUpsell);
      document.dispatchEvent(new CustomEvent("pdp:upsell:updated", { detail: { root: nextUpsell } }));
    } else if (currentUpsell && !nextUpsell) {
      currentUpsell.remove();
    } else if (!currentUpsell && nextUpsell) {
      const anchor = productInfo.querySelector("[data-pvs]") || productInfo.querySelector("product-form");
      if (anchor) {
        anchor.insertAdjacentElement("afterend", nextUpsell);
        document.dispatchEvent(new CustomEvent("pdp:upsell:updated", { detail: { root: nextUpsell } }));
      }
    }
  }

  async function fetchAndUpdateProductInfo(root, variantId) {
    const productInfo = getProductInfo(root);
    if (!productInfo) return false;

    const ids = getSectionIds(productInfo);
    const sectionId = ids.sectionId;
    const destSection = ids.destSection;
    const url = getProductUrl(productInfo);

    if (!sectionId || !destSection || !url) {
      log("skip fetchAndUpdateProductInfo because section/url missing", {
        sectionId,
        destSection,
        url
      });
      return false;
    }

    const prev = FETCH_STATE.get(productInfo);
    if (prev) prev.abort();

    const controller = new AbortController();
    FETCH_STATE.set(productInfo, controller);

    const requestUrl =
      `${url}?variant=${encodeURIComponent(String(variantId))}` +
      `&section_id=${encodeURIComponent(String(sectionId))}`;

    log("fetching product info", requestUrl);

    try {
      const res = await fetch(requestUrl, {
        signal: controller.signal,
        credentials: "same-origin"
      });

      const text = await res.text();
      if (controller.signal.aborted) return false;

      const html = new DOMParser().parseFromString(text, "text/html");

      updateFromFetchedHTML(productInfo, html, sectionId, destSection);
      updateVariantInputsInProductInfo(productInfo, variantId);
      updateURL(productInfo, url, variantId);

      return true;
    } catch (e) {
      if (e && e.name === "AbortError") return false;
      console.error("[PVSEL] fetchAndUpdateProductInfo error", e);
      return false;
    }
  }

  function getOptionLabelForToggle(optTitle, optionObj) {
    // Prefer the human-readable option title (e.g. "Garnfarbe") over the handleized
    // key, which is lowercase and strips umlauts ("Größe" -> "groesse").
    const raw = optTitle || optionObj?.title || optionObj?.key || "";
    let s = String(raw).trim();
    if (!s) return "";
    // Capitalize the first letter for the expanded option toggle label.
    s = s.charAt(0).toUpperCase() + s.slice(1);
    // German plural for option names ending in -e (Farbe -> Farben, Größe -> Größen).
    if (/e$/i.test(s)) s += "n";
    return s;
  }

  function getVariantImageFromData(variant) {
    const a =
      variant?.featured_image?.src ??
      variant?.featured_image ??
      variant?.image?.src ??
      variant?.image ??
      variant?.featured_media?.preview_image?.src ??
      variant?.featured_media?.src ??
      variant?.variant_image ??
      variant?.variant_image_src ??
      null;

    return a ? String(a) : null;
  }

  const PRODUCT_JS_CACHE = new Map();

  function getHandleFromUrl(url) {
    if (!url) return "";

    try {
      const u = new URL(url, window.location.origin);
      const parts = u.pathname.split("/").filter(Boolean);
      const i = parts.indexOf("products");
      if (i !== -1 && parts[i + 1]) return parts[i + 1];
      return "";
    } catch (e) {
      return "";
    }
  }

  async function loadProductJs(handle) {
    if (!handle) return null;
    if (PRODUCT_JS_CACHE.has(handle)) return PRODUCT_JS_CACHE.get(handle);

    const p = (async () => {
      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
          credentials: "same-origin",
          headers: { Accept: "application/json" }
        });
        if (!res.ok) return null;
        return await res.json();
      } catch (e) {
        return null;
      }
    })();

    PRODUCT_JS_CACHE.set(handle, p);
    return p;
  }

  async function getVariantImageById(root, variantId) {
    const productInfo = getProductInfo(root);
    const url = getProductUrl(productInfo) || window.location.pathname;
    const handle = getHandleFromUrl(url);
    const pj = await loadProductJs(handle);

    if (!pj || !Array.isArray(pj.variants)) return null;

    const v = pj.variants.find((x) => String(x.id) === String(variantId));
    if (!v) return null;

    if (v.featured_image && v.featured_image.src) return String(v.featured_image.src);
    if (typeof v.featured_image === "string" && v.featured_image) return String(v.featured_image);
    if (typeof v.image === "string" && v.image) return String(v.image);
    if (v.image && v.image.src) return String(v.image.src);

    return null;
  }

	  function setMainImage(root, src) {
	    if (!src) return false;

	    const productInfo = getProductInfo(root);

    const candidates = [];
    if (productInfo) {
      candidates.push(
        productInfo.querySelector("media-gallery img"),
        productInfo.querySelector(".product__media img"),
        productInfo.querySelector(".product-media img"),
        productInfo.querySelector("img[fetchpriority]")
      );
    }

    candidates.push(
      document.querySelector("media-gallery img"),
      document.querySelector(".product__media img"),
      document.querySelector(".product-media img"),
      document.querySelector("img[fetchpriority]")
    );

    const img = candidates.find(Boolean);
    if (!img) return false;

    try {
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      img.removeAttribute("data-srcset");
      img.removeAttribute("data-sizes");
      img.srcset = "";
      img.src = src;

      const pic = img.closest("picture");
      if (pic) {
        pic.querySelectorAll("source").forEach((s) => {
          s.removeAttribute("srcset");
          s.removeAttribute("sizes");
          s.removeAttribute("data-srcset");
          s.removeAttribute("data-sizes");
        });
      }

	      const a = img.closest("a");
	      if (a && a.getAttribute("href")) {
	        a.setAttribute("href", src);
	      }

	      setMainThumbnail(root, src);
	      return true;
	    } catch (e) {
	      return false;
	    }
	  }

  function imageUrlWithWidth(src, width) {
    if (!src) return "";

    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set("width", String(width));
      return url.toString();
    } catch (e) {
      const separator = src.indexOf("?") === -1 ? "?" : "&";
      return `${src}${separator}width=${width}`;
    }
  }

  function setImageSource(img, src) {
    if (!img || !src) return false;

    const sizedSrc = imageUrlWithWidth(src, 220);

    img.removeAttribute("srcset");
    img.removeAttribute("sizes");
    img.removeAttribute("data-srcset");
    img.removeAttribute("data-sizes");
    img.srcset = "";
    img.src = sizedSrc;

    return true;
  }

  function setMainThumbnail(root, src) {
    if (!src) return false;

    const productInfo = getProductInfo(root);
    const candidates = [];

    if (productInfo) {
      candidates.push(
        productInfo.querySelector('[id^="Slider-Thumbnails"] button[aria-current="true"] img'),
        productInfo.querySelector('[id^="Slider-Thumbnails"] .thumbnail-list__item:not([style*="display: none"]) button img'),
        productInfo.querySelector('[id^="Slider-Thumbnails"] button img')
      );
    }

    candidates.push(
      document.querySelector('[id^="Slider-Thumbnails"] button[aria-current="true"] img'),
      document.querySelector('[id^="Slider-Thumbnails"] .thumbnail-list__item:not([style*="display: none"]) button img'),
      document.querySelector('[id^="Slider-Thumbnails"] button img')
    );

    const thumb = candidates.find(Boolean);
    return setImageSource(thumb, src);
  }

  async function updateVariantImage(root, variant, preferredSrc) {
    // The PDP variant gallery (sections/main-product.liquid) swaps all images itself for colours with
    // gallery images; swapping the main image here too would download a second size and flash twice.
    if (
      variant && variant.id &&
      typeof window.__variantGalleryHandles === "function" &&
      window.__variantGalleryHandles(variant.id)
    ) {
      return true;
    }

    if (preferredSrc) {
      setMainImage(root, preferredSrc);
      return true;
    }

    const src1 = getVariantImageFromData(variant);
    if (src1) {
      setMainImage(root, src1);
      return true;
    }

    const vid = variant?.id;
    if (!vid) return false;

    const src2 = await getVariantImageById(root, vid);
    if (src2) {
      setMainImage(root, src2);
      return true;
    }

    return false;
  }

  function isVariantOnSale(variant) {
    const compare = parseInt(variant?.compare_at_price || 0, 10);
    const price = parseInt(variant?.price || 0, 10);
    return !!compare && compare > price;
  }

  function getBadgeScope(root) {
    return getProductInfo(root) || root || document;
  }

  function findBadgesWrap(root) {
    const scope = getBadgeScope(root);

    return (
      scope.querySelector(".pdp__badges") ||
      document.querySelector(".pdp__badges") ||
      null
    );
  }

  function findSaleBadge(root) {
    const wrap = findBadgesWrap(root);
    if (!wrap) return null;

    return wrap.querySelector(".badge__is_on_sale:not([data-variant-badge])");
  }

  function findSoldOutBadge(root) {
    const wrap = findBadgesWrap(root);
    if (!wrap) return null;

    return wrap.querySelector(".badge__is_sold");
  }

  function ensureSoldOutBadge(root) {
    const wrap = findBadgesWrap(root);
    if (!wrap) return null;

    let badge = findSoldOutBadge(root);
    if (badge) return badge;

    badge = document.createElement("span");
    badge.className = "badge badge__is_sold";
    badge.setAttribute("data-system-badge", "");
    badge.textContent = pickString("soldOut", "Sold out");

    const anchor = wrap.querySelector(".badge__influencer");
    wrap.insertBefore(badge, anchor || null);

    return badge;
  }

  function ensureSaleBadge(root) {
    const wrap = findBadgesWrap(root);
    if (!wrap) return null;

    let badge = findSaleBadge(root);
    if (badge) return badge;

    badge = document.createElement("span");
    badge.className = "badge badge__is_on_sale";
    badge.setAttribute("data-product-badge", "");
    badge.textContent = pvselString("sale", "Sale");
    wrap.appendChild(badge);

    return badge;
  }

  function parseVariantBadges(root) {
    const scope = getBadgeScope(root);
    const script =
      (scope && scope.querySelector("[data-pdp-variant-badges]")) ||
      document.querySelector("[data-pdp-variant-badges]");

    if (!script) return null;
    if (script.__pdpVariantBadges) return script.__pdpVariantBadges;

    try {
      script.__pdpVariantBadges = JSON.parse(script.textContent || "{}");
      return script.__pdpVariantBadges;
    } catch (error) {
      return null;
    }
  }

  function getVariantBadges(root, variant) {
    const variantId = variant && variant.id != null ? String(variant.id) : "";
    if (!variantId) return [];

    const data = parseVariantBadges(root);
    const raw = data && data.by_variant_id && data.by_variant_id[variantId];
    if (!Array.isArray(raw)) return [];

    return raw.map((badge) => String(badge || "").trim()).filter(Boolean);
  }

  function badgeClass(label) {
    const key = String(label || "").trim().toLowerCase();
    if (!key) return "badge__custom";
    if (key.includes("sale")) return "badge__is_on_sale";
    if (key.includes("bestseller")) return "badge__bestseller";
    if (key.includes("neue farben") || key.includes("neue-farben")) return "badge__new_farben";
    if (key === "neu") return "badge__neu";
    return "badge__custom";
  }

  function badgeText(label) {
    const text = String(label || "").trim();
    const key = text.toLowerCase();
    if (key.includes("sale")) return pvselString("sale", "Sale");
    if (key.includes("bestseller")) return pvselString("bestseller", "Bestseller");
    return text.toLowerCase() === "neu" ? pickString("badgeNew", "NEU").toUpperCase() : text;
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function renderPvselVariantBadges(variant) {
    const badges = Array.isArray(variant?.badges)
      ? variant.badges.map((badge) => String(badge || "").trim()).filter(Boolean)
      : [];

    if (!badges.length) return "";

    return (
      '<span class="pvsel__variant-badges" aria-hidden="true">' +
      badges
        .slice(0, 2)
        .map((label) => {
          const className = "pvsel__variant-badge " + badgeClass(label);
          return '<span class="' + className + '">' + escapeHtml(badgeText(label)) + "</span>";
        })
        .join("") +
      "</span>"
    );
  }

  function syncVariantBadges(root, variant) {
    const wrap = findBadgesWrap(root);
    if (!wrap || !variant) return false;

    const badges = getVariantBadges(root, variant);
    wrap.querySelectorAll("[data-variant-badge]").forEach((node) => node.remove());

    const productBadges = wrap.querySelectorAll("[data-product-badge]");

    if (!badges.length) {
      productBadges.forEach(showEl);
      return false;
    }

    productBadges.forEach(hideEl);

    const anchor = wrap.querySelector("[data-system-badge], .badge__influencer");
    badges.forEach((label) => {
      const node = document.createElement("span");
      node.className = `badge ${badgeClass(label)}`;
      node.setAttribute("data-variant-badge", "");
      node.textContent = badgeText(label);
      wrap.insertBefore(node, anchor || null);
    });

    return true;
  }

  function showEl(el) {
    if (!el) return;
    el.hidden = false;
    el.style.display = "";
    el.classList.remove("hidden");
  }

  function hideEl(el) {
    if (!el) return;
    el.hidden = true;
    el.style.display = "none";
    el.classList.add("hidden");
  }

  function updateMainImageBadges(root, variant) {
    const wrap = findBadgesWrap(root);
    if (!wrap || !variant) return;

    const onSale = isVariantOnSale(variant);
    const available = !!variant.available;
    const hasVariantBadges = syncVariantBadges(root, variant);

    let saleBadge = findSaleBadge(root);
    let soldBadge = findSoldOutBadge(root);

    if (hasVariantBadges) {
      if (saleBadge) hideEl(saleBadge);
    } else if (onSale) {
      saleBadge = ensureSaleBadge(root);
      showEl(saleBadge);
    } else if (saleBadge) {
      hideEl(saleBadge);
    }

    if (!available) {
      soldBadge = ensureSoldOutBadge(root);
      showEl(soldBadge);
    } else if (soldBadge) {
      hideEl(soldBadge);
    }

    const hasVisibleBadge = Array.from(wrap.children).some((el) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.hidden) return false;
      if (el.classList.contains("hidden")) return false;
      if (el.style.display === "none") return false;
      return true;
    });

    if (hasVisibleBadge) {
      showEl(wrap);
    } else {
      hideEl(wrap);
    }

    log("updateMainImageBadges", {
      variantId: variant?.id,
      onSale,
      available,
      hasVariantBadges,
      saleBadgeExists: !!saleBadge,
      soldBadgeExists: !!soldBadge,
      wrapVisible: hasVisibleBadge
    });
  }

  function getSubmitBtn(root) {
    const productInfo = getProductInfo(root);
    return (
      (productInfo && productInfo.querySelector(".product-form__submit")) ||
      root.querySelector(".product-form__submit") ||
      document.querySelector(".product-form__submit") ||
      null
    );
  }

  function getSubmitTextNode(btn) {
    if (!btn) return null;
    return (
      btn.querySelector(".product-form__submit-text") ||
      btn.querySelector("[data-add-to-cart-text]") ||
      btn.querySelector("span") ||
      btn
    );
  }

  function pvselString(key, fallback) {
    const S = (window.gwaStrings && window.gwaStrings.pvsel) || {};
    return S[key] ? String(S[key]) : fallback;
  }

  function pickString(key, fallback) {
    try {
      if (window.theme && window.theme.strings && window.theme.strings[key]) {
        return String(window.theme.strings[key]);
      }
      if (window.variantStrings && window.variantStrings[key]) {
        return String(window.variantStrings[key]);
      }
      if (window.gwaStrings && window.gwaStrings[key]) {
        return String(window.gwaStrings[key]);
      }
    } catch (e) {}
    return fallback;
  }

  const SUBMIT_DEFAULT_CACHE = new WeakMap();

  function getSubmitScope(root) {
    return getProductInfo(root) || document;
  }

  function ensureDefaultSubmitLabel(root, btn) {
    if (!btn) return;

    const scope = getSubmitScope(root);
    const cached = SUBMIT_DEFAULT_CACHE.get(scope);
    if (cached && String(cached).trim()) return;

    const t = getSubmitTextNode(btn);
    const txt = t ? String(t.textContent || "").trim() : "";
    const addLabel =
      btn.getAttribute("data-label-add") ||
      pickString("addToCart", "") ||
      txt ||
      "Add to cart";

    SUBMIT_DEFAULT_CACHE.set(scope, addLabel);
  }

  function setSubmitState(root, variant) {
    const btn = getSubmitBtn(root);
    if (!btn) return;

    const productInfo = getProductInfo(root);
    const productForm =
      (productInfo && productInfo.querySelector('product-form[data-pdp-variation-set-missing="true"]')) ||
      root.querySelector('product-form[data-pdp-variation-set-missing="true"]');

    const variationSetMissing = variant?.variation_set_missing === true;

    if (productForm || variationSetMissing) {
      const t = getSubmitTextNode(btn);
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-disabled');
      if (t) t.textContent = btn.getAttribute('data-label-unavailable') || pickString("unavailable", "Unavailable");
      return;
    }

    ensureDefaultSubmitLabel(root, btn);

    const scope = getSubmitScope(root);
    const defaultLabel = SUBMIT_DEFAULT_CACHE.get(scope) || pickString("addToCart", "Add to cart");

    const t = getSubmitTextNode(btn);
    const available = !!(variant && variant.available);

    if (available) {
      btn.disabled = false;
      btn.removeAttribute("aria-disabled");
      btn.classList.remove("is-disabled");
      if (t) t.textContent = defaultLabel;
    } else {
      btn.disabled = true;
      btn.setAttribute("aria-disabled", "true");
      btn.classList.add("is-disabled");

      const label =
        pickString("soldOut", "") ||
        pickString("unavailable", "") ||
        btn.getAttribute("data-label-soldout") ||
        btn.getAttribute("data-label-unavailable") ||
        "Sold out";

      if (t) t.textContent = label;
    }
  }

  function getPreferredImgFromBtn(btn) {
    if (!btn) return null;
    const ds = btn.dataset || {};
    const v =
      ds.variantImage ||
      ds.variantImg ||
      ds.image ||
      btn.getAttribute("data-variant-image") ||
      btn.getAttribute("data-variant-img") ||
      btn.getAttribute("data-image") ||
      null;
    return v ? String(v) : null;
  }

  const initializedRoots = new WeakSet();

  function initOne(root) {
    if (initializedRoots.has(root)) return;
    const script = $("[data-pvsel-data]", root);
    const mount = $("[data-pvsel-mount]", root);

    if (!script || !mount) {
      log("skip root because script or mount missing", { root, script, mount });
      return;
    }

    let data;
    try {
      data = JSON.parse(script.textContent || "{}");
    } catch (e) {
      console.error("[PVSEL] JSON parse error", e, script.textContent);
      return;
    }

    const variants = data.variants || [];
    if (!variants.length) {
      log("no variants in data", data);
      return;
    }

    groupLog("raw data", data);
    groupLog("variants", variants.map((v) => ({
      id: v?.id,
      title: v?.title,
      available: v?.available,
      options: v?.options,
      option1: v?.option1,
      option2: v?.option2,
      option3: v?.option3,
      option_values: v?.option_values
    })));

    const groups = groupOptionValues(variants, data);
    const index = buildVariantIndex(variants, data);
    const variantIdentity = groups.length === 1 && groups[0].mode === "variant-id";
    const variantsById = new Map(variants.map((variant) => [String(variant.id), variant]));

    groupLog("final groups", groups.map((g) => ({
      optTitle: g.optTitle,
      mode: g.mode,
      values: (g.valuesSorted || []).map((x) => getVariantLabelValue(x))
    })));

    const initialLimit = parseInt(root.dataset.initialLimit || "12", 10) || 12;
    const formId = root.dataset.productFormId;

    const initialId = data.initialVariantId;
    let current =
      variants.find((v) => String(v.id) === String(initialId)) ||
      variants.find((v) => v.available) ||
      variants[0];

    const selection = {};

    const initialOptionNames = getProductOptionNames(data, variants);
    const initialOptionValues = getVariantOptionArray(current, initialOptionNames);

    if (initialOptionNames.length && initialOptionValues.length) {
      initialOptionNames.forEach((name, i) => {
        if (initialOptionValues[i] != null && initialOptionValues[i] !== "") {
          selection[name] = variantIdentity ? String(current.id) : initialOptionValues[i];
        }
      });
    } else if (Array.isArray(current.option_values) && current.option_values.length) {
      for (const ov of current.option_values || []) {
        const optTitle = ov?.option?.title;
        if (!optTitle) continue;
        selection[optTitle] = getVariantLabelValue(ov);
      }
    } else {
      log("no option values found for initial selection", current);
    }

    log("initial current variant", current);
    log("initial selection", selection);

    const expanded = {};
    initializedRoots.add(root);

    function selectionValue(ov) {
      return variantIdentity ? ov._variant_id : getVariantLabelValue(ov);
    }

    function selectedVariant(next) {
      return variantIdentity ? variantsById.get(next[groups[0].optTitle]) : findVariant(index, next);
    }

    function variantForValue(optTitle, ov) {
      const next = Object.assign({}, selection);
      next[optTitle] = selectionValue(ov);
      return selectedVariant(next);
    }

    async function selectBy(optTitle, ov, btnEl) {
      selection[optTitle] = selectionValue(ov);

      const v = selectedVariant(selection);
      if (!v) {
        log("variant not found on select", { optTitle, value: getVariantLabelValue(ov), selection });
        return;
      }

      current = v;

      if (formId) syncVariantId(formId, v.id);

      // Early signal (before the product-info fetch below) so the variant gallery can swap images right away.
      document.dispatchEvent(new CustomEvent("pvsel:variant-select", { detail: { root, variant: v, variantId: String(v.id) } }));

      setSubmitState(root, v);

      const preferredSrc = getPreferredImgFromBtn(btnEl);

      await updateVariantImage(root, v, preferredSrc);
      updateMainImageBadges(root, v);

      await fetchAndUpdateProductInfo(root, v.id);

      setSubmitState(root, v);
      await updateVariantImage(root, v, preferredSrc);
      updateMainImageBadges(root, v);

      paint();
      dispatchPvselVariantChange(root, v, preferredSrc);
    }

    function isTextGroup(g) {
      for (const ov of g.valuesSorted || []) {
        const st = norm(ov?.swatch_type);
        const hasImg = st === "image" && !!ov?.swatch_image;
        const hasColor = st === "color" && !!ov?.swatch_color;
        if (hasImg || hasColor) return false;
      }
      return true;
    }

    function paint() {
      mount.innerHTML = "";

      log("paint groups", groups.map((g) => ({
        optTitle: g.optTitle,
        count: (g.valuesSorted || []).length
      })));

      for (const g of groups) {
        const optTitle = g.optTitle;
        const optNorm = norm(optTitle);

        const wrap = document.createElement("div");
        wrap.className = "pvsel__group";

        const title = document.createElement("div");
        title.className = "pvsel__title";

        const selectedValue = selection[optTitle] || "";
        const selectedOption = g.valuesSorted.find((ov) => selectionValue(ov) === selectedValue);
        const selectedLabel = selectedOption ? getVariantLabelValue(selectedOption) : selectedValue;

        const selectedVariantForTitle = selectedVariant(selection) || current;
        const selectedDisplayLabel = isColorOptionTitle(optTitle)
          ? formatColorOptionLabel(selectedLabel, getVariantColorNumber(selectedVariantForTitle), data.colorLabelOverrides)
          : selectedLabel;

        title.innerHTML =
          '<div class="pvsel__titleLeft">' +
          '<span class="pvsel__optName">' +
          escapeHtml(optTitle) +
          ": </span>" +
          '<span class="pvsel__optSelected">' +
          escapeHtml(selectedDisplayLabel) +
          "</span>" +
          "</div>";

        wrap.appendChild(title);

        const grid = document.createElement("div");
        grid.className = "pvsel__grid";
        if (isTextGroup(g)) grid.classList.add("pvsel__grid_text");

        const isExp = !!expanded[optNorm];
        let vals = isExp ? g.valuesSorted.slice() : g.valuesSorted.slice(0, initialLimit);

        const selected = selectedOption;
        if (!isExp && selected && !vals.includes(selected)) vals.push(selected);

        log("render group", {
          optTitle,
          selectedLabel,
          total: g.valuesSorted.length,
          rendered: vals.map((x) => getVariantLabelValue(x))
        });

        for (const ov of vals) {
          const vFor = variantForValue(optTitle, ov);
          const isAvailable = vFor ? !!vFor.available && vFor.variation_set_missing !== true : false;
          const isActive = selectionValue(ov) === selectedValue;

          const btn = document.createElement("button");
          btn.type = "button";
          btn.dataset.pvselValue = getVariantLabelValue(ov);
          btn.setAttribute("aria-pressed", String(isActive));
          btn.className =
            "pvsel__sw" + (isActive ? " is-active" : "") + (!isAvailable ? " is-oos" : "");

          const fullLabel = String(getVariantLabelValue(ov) || "").trim();
          const tooltipLabel = isColorOptionTitle(optTitle)
            ? formatColorOptionLabel(fullLabel, getVariantColorNumber(vFor), data.colorLabelOverrides)
            : fullLabel;
          if (fullLabel) {
            btn.setAttribute("aria-label", tooltipLabel);
            btn.dataset.swatchTooltip = tooltipLabel;
          }

          if (vFor && vFor.id != null) btn.dataset.variantId = String(vFor.id);
          btn.dataset.available = isAvailable ? "1" : "0";

          const imgFromVariant = vFor ? getVariantImageFromData(vFor) : null;
          const imgFromOv =
            ov?.variant_image || ov?.variant_image_src || ov?.image || ov?.img || null;
          const imgToUse = imgFromVariant || (imgFromOv ? String(imgFromOv) : null);
          if (imgToUse) btn.setAttribute("data-variant-image", imgToUse);

          const off = vFor ? pctOff(vFor.compare_at_price, vFor.price) : 0;
          const st = norm(ov?.swatch_type);

          let inner = "";
          if (st === "image" && ov?.swatch_image) {
            inner =
              '<span class="pvsel__img" style="background-image:url(\'' +
              ov.swatch_image +
              "')\"></span>";
          } else if (st === "color" && ov?.swatch_color) {
            inner = '<span class="pvsel__dot" style="background:' + ov.swatch_color + '"></span>';
          } else {
            btn.classList.add("is-text");
            inner = '<span class="pvsel__txt">' + escapeHtml(fullLabel) + "</span>";
          }

          btn.innerHTML =
            inner +
            renderPvselVariantBadges(vFor) +
            (off ? '<span class="pvsel__badge">' + off + "%</span>" : "") +
            (!isAvailable ? '<span class="pvsel__x">×</span>' : "");

          btn.addEventListener("click", function () {
            selectBy(optTitle, ov, btn);
          });

          grid.appendChild(btn);
        }

        if (g.valuesSorted.length > initialLimit) {
          const toggle = document.createElement("button");
          toggle.type = "button";
          toggle.className = "pvsel__toggle pvsel__sw is-text";
          toggle.setAttribute("aria-expanded", String(isExp));

          const optLabel = getOptionLabelForToggle(optTitle, g.option);
          toggle.textContent = isExp
            ? pickString("showLess", "Show less")
            : pickString("showAllValues", "Show all").replace("[count]", g.valuesSorted.length).replace("[option]", optLabel);

          toggle.addEventListener("click", function () {
            expanded[optNorm] = !expanded[optNorm];
            paint();
          });

          grid.appendChild(toggle);
        }

        wrap.appendChild(grid);
        mount.appendChild(wrap);
      }
    }

    // Keep the real Liquid controls in place on first load. Rebuilding an empty
    // mount after DOMContentLoaded moved the price and purchase button down.
    // Only user-initiated selections/expansion need a new paint.
    if (mount.hasAttribute("data-pvsel-server-rendered") && !variantIdentity) {
      for (const groupElement of $$("[data-pvsel-option]", mount)) {
        const g = groups.find((group) => norm(group.optTitle) === norm(groupElement.dataset.pvselOption));
        if (!g) continue;
        for (const btn of $$("[data-pvsel-value]", groupElement)) {
          const ov = g.valuesSorted.find((value) => norm(getVariantLabelValue(value)) === norm(btn.dataset.pvselValue));
          if (!ov) continue;
          const variant = variantForValue(g.optTitle, ov);
          const available = !!variant?.available && variant.variation_set_missing !== true;
          btn.dataset.available = available ? "1" : "0";
          btn.classList.toggle("is-oos", !available);
          if (variant?.id != null) btn.dataset.variantId = String(variant.id);
          const variantImage = getVariantImageFromData(variant);
          if (variantImage) btn.dataset.variantImage = variantImage;
          // Badges are absolutely positioned and do not alter the reserved layout.
          btn.insertAdjacentHTML("beforeend", renderPvselVariantBadges(variant));
          if (!available && !btn.querySelector(".pvsel__x")) {
            btn.insertAdjacentHTML("beforeend", '<span class="pvsel__x">×</span>');
          }
          btn.addEventListener("click", () => selectBy(g.optTitle, ov, btn));
        }
        const toggle = $("[data-pvsel-toggle]", groupElement);
        toggle?.addEventListener("click", () => {
          expanded[norm(g.optTitle)] = true;
          paint();
        });
      }
    } else {
      paint();
    }
    setSubmitState(root, current);
    updateMainImageBadges(root, current);
  }

  function init() {
    const roots = $$("[data-pvsel]");
    log("found roots", roots.length);
    roots.forEach(initOne);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", init);
})();
